import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowClockwise,
  Buildings,
  CaretLeft,
  CaretRight,
  DownloadSimple,
  PaperPlaneTilt,
  WarningCircle,
} from '@phosphor-icons/react';
import { fetchCustomerSegments, syncCustomerSegmentsToKlaviyo } from '../../api/customerSegments';
import { PageSummaryStats } from '../../components/dashboard/PageSummaryStats';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import { ConfidenceStrip } from '../../components/journeys/ConfidenceStrip';
import { DataSourceChip } from '../../components/journeys/DataSourceChip';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAppNavigation } from '../../context/AppNavigationContext';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';
import { usePersistedState } from '../../hooks/usePersistedState';
import { LIVE_DATA_SOURCE_LABEL } from '../../lib/journeyApiFilters';
import { cn } from '../../lib/utils';

const PREFS_KEY = 'vi.customerSegments.prefs.v1';
const PAGE_SIZE = 25;

function isoDate(date) {
  return date.toISOString().slice(0, 10);
}

function defaultStartDate() {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return isoDate(date);
}

const DEFAULT_FILTERS = {
  start: defaultStartDate(),
  end: isoDate(new Date()),
  topPercent: '10',
  limit: '100',
};

const churnTone = {
  low: 'bg-emerald-50 text-emerald-800 ring-emerald-200/80',
  medium: 'bg-amber-50 text-amber-900 ring-amber-200/80',
  high: 'bg-orange-50 text-orange-900 ring-orange-200/80',
  dormant: 'bg-[#F8F1E3] text-[#5D2E17] ring-[#C9A227]/40',
};

function formatNumber(value) {
  return new Intl.NumberFormat('da-DK').format(Number(value || 0));
}

function formatMoney(value, currencyCode) {
  return `${currencyCode || 'DKK'} ${formatNumber(Math.round(Number(value || 0)))}`;
}

function ChurnBadge({ level }) {
  return (
    <span
      className={cn(
        'inline-flex min-w-[4.5rem] items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold capitalize ring-1',
        churnTone[level] || 'bg-slate-50 text-slate-600 ring-slate-200'
      )}
    >
      {level || 'unknown'}
    </span>
  );
}

const BEHAVIOR_LABELS = {
  high_value: 'High value',
  vip_buyer: 'VIP',
  family_buyer: 'Family',
  food_spender: 'Food',
  app_user: 'App',
  ticket_buyer: 'Tickets',
  attended_event: 'Attended',
  one_time_buyer: 'One-time',
  frequent_buyer: 'Frequent',
  top_decile_spender: 'Top 10%',
  needs_reengagement: 'Re-engage',
  standard_customer: 'Standard',
};

function behaviorLabel(segment) {
  return BEHAVIOR_LABELS[segment] || String(segment || '').replaceAll('_', ' ');
}

function BehaviorTags({ segments = [] }) {
  if (!segments.length) {
    return <span className="text-xs text-slate-400">—</span>;
  }

  const visible = segments.slice(0, 3);
  const extra = segments.length - visible.length;

  return (
    <div className="flex min-w-[11rem] flex-wrap gap-1">
      {visible.map((segment) => (
        <span
          key={segment}
          title={behaviorLabel(segment)}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
        >
          {behaviorLabel(segment)}
        </span>
      ))}
      {extra > 0 ? (
        <span className="rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-400">
          +{extra}
        </span>
      ) : null}
    </div>
  );
}

function ActivityCell({ segment }) {
  const visits = Number(segment.visitFrequencyPer30Days || 0);
  return (
    <div className="min-w-[7.5rem] whitespace-nowrap">
      <div className="text-sm font-semibold tabular-nums text-[#201C35]">
        {visits}
        <span className="ml-1 text-[11px] font-medium text-slate-400">/30d</span>
      </div>
      <div className="mt-0.5 flex gap-2 text-[10px] font-medium tabular-nums text-slate-400">
        <span title="Event scans">ACP {segment.eventAttendanceCount || 0}</span>
        <span title="POS activity">POS {segment.posActivityCount || 0}</span>
        <span title="App activity">APP {segment.appActivityCount || 0}</span>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="flex min-w-[9.5rem] flex-1 basis-[9.5rem] flex-col gap-1 sm:basis-[11rem]">
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
        {label}
      </span>
      {children}
    </label>
  );
}

const filterControlClass =
  'h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 shadow-sm outline-none transition hover:bg-slate-50 focus:border-[var(--brand-primary,#1A428A)]/40 focus:ring-2 focus:ring-[var(--brand-primary,#1A428A)]/15';

function friendlySegmentsError(rawMessage) {
  const message = String(rawMessage || '').toLowerCase();
  if (message.includes('accountid') || message.includes('account id')) {
    return {
      variant: 'info',
      icon: Buildings,
      title: 'Select a tenant first',
      message:
        'Open a customer account from Tenants so we know whose segment scores to show.',
    };
  }
  return {
    variant: 'error',
    icon: WarningCircle,
    title: "We couldn't load customer segments",
    message:
      'Something went wrong while fetching this data. Try refreshing, or adjust the date range and try again.',
  };
}

function buildPageItems(current, total) {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1].filter((p) => p >= 1 && p <= total));
  return [...pages].sort((a, b) => a - b);
}

function TablePagination({ page, pageCount, pageSize, total, onChange }) {
  if (total <= 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const pageItems = buildPageItems(page, pageCount);

  return (
    <div className="flex flex-col items-center gap-1.5 px-1 py-1">
      <p className="text-[11px] text-slate-500">
        Showing{' '}
        <span className="font-semibold text-slate-700">
          {formatNumber(from)} – {formatNumber(to)}
        </span>{' '}
        of <span className="font-semibold text-slate-700">{formatNumber(total)}</span>
      </p>
      <div className="flex items-center gap-1" role="navigation" aria-label="Table pagination">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
        >
          <CaretLeft size={12} weight="bold" aria-hidden="true" />
        </button>
        {pageItems.map((item, index) => {
          const prev = pageItems[index - 1];
          const showGap = prev != null && item - prev > 1;
          const active = item === page;
          return (
            <React.Fragment key={item}>
              {showGap ? (
                <span className="px-0.5 text-[11px] text-slate-400" aria-hidden="true">
                  …
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => onChange(item)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'inline-flex h-7 w-7 items-center justify-center rounded border text-[11px] font-semibold transition',
                  active
                    ? 'border-[var(--brand-primary,#1A428A)] bg-[var(--brand-primary,#1A428A)] text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                )}
              >
                {item}
              </button>
            </React.Fragment>
          );
        })}
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => onChange(page + 1)}
          className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
        >
          <CaretRight size={12} weight="bold" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function exportSegments(data) {
  if (!data) return;
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `venue-intelligence-customer-segments-${data.period?.end || 'export'}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function CustomerSegmentsPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const { setHeaderTrailing } = useAppNavigation();
  const [filters, setFilters] = usePersistedState(PREFS_KEY, DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(() => ({ ...DEFAULT_FILTERS, ...filters }));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [page, setPage] = useState(1);
  const sideRailRef = useRef(null);
  const [sideRailHeight, setSideRailHeight] = useState(null);
  const [isWideLayout, setIsWideLayout] = useState(false);

  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;
  const needsTenant = Boolean(isSuperAdmin && !viewingTenant);

  const loadSegments = useCallback(
    async (nextFilters = filters) => {
      if (isSuperAdmin && !accountId) {
        setLoading(false);
        setData(null);
        setError('NEEDS_TENANT');
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await fetchCustomerSegments({
          ...nextFilters,
          accountId,
        });
        setData(response);
        setPage(1);
      } catch (err) {
        setError(err?.message || 'Failed to load customer segments');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [accountId, filters, isSuperAdmin]
  );

  const handleKlaviyoSync = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await syncCustomerSegmentsToKlaviyo({
        ...filters,
        accountId,
      });
      setSyncResult({
        type: 'success',
        message: `Sent ${result.profileCount} customer segment profiles to Klaviyo.`,
      });
    } catch (err) {
      setSyncResult({
        type: 'error',
        message: err?.message || 'Failed to sync customer segments to Klaviyo',
      });
    } finally {
      setSyncing(false);
    }
  }, [accountId, filters]);

  useEffect(() => {
    loadSegments();
  }, [filters.start, filters.end, filters.topPercent, filters.limit, accountId, loadSegments]);

  useEffect(() => {
    setHeaderTrailing(
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => loadSegments(filters)}
          className="h-9 gap-1.5 !rounded-full"
        >
          <ArrowClockwise size={14} weight="bold" aria-hidden="true" />
          Refresh
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => exportSegments(data)}
          disabled={!data}
          className="h-9 gap-1.5 !rounded-full"
        >
          <DownloadSimple size={14} weight="bold" aria-hidden="true" />
          Export
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleKlaviyoSync}
          disabled={!data || syncing}
          className="h-9 gap-1.5 !rounded-full bg-[var(--brand-primary,#1A428A)] hover:bg-[#163872]"
        >
          <PaperPlaneTilt size={14} weight="bold" aria-hidden="true" />
          {syncing ? 'Syncing…' : 'Sync to Klaviyo'}
        </Button>
      </div>
    );
    return () => setHeaderTrailing(null);
  }, [data, syncing, filters, loadSegments, handleKlaviyoSync, setHeaderTrailing]);

  const segments = useMemo(() => data?.segments || [], [data?.segments]);
  const summary = data?.summary || {};
  const churnCounts = summary.churnRiskCounts || {};

  const pageCount = Math.max(1, Math.ceil(segments.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return segments.slice(start, start + PAGE_SIZE);
  }, [segments, safePage]);

  const highSpenderCandidates = useMemo(
    () => segments.filter((row) => row.highSpender).slice(0, 6),
    [segments]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(min-width: 1280px)');
    const sync = () => setIsWideLayout(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const node = sideRailRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const syncHeight = () => {
      const next = Math.round(node.getBoundingClientRect().height);
      setSideRailHeight((current) => (current === next ? current : next));
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, error, highSpenderCandidates.length, summary.customerCount]);

  const summaryStats = useMemo(
    () => [
      {
        title: 'Scored customers',
        value: formatNumber(summary.customerCount),
        change: '—',
        trend: 'neutral',
        context:
          data?.period?.start && data?.period?.end
            ? `${data.period.start} → ${data.period.end}`
            : 'Selected window',
        featured: true,
      },
      {
        title: 'High spenders',
        value: formatNumber(summary.highSpenderCount),
        change: '—',
        trend: 'neutral',
        context: `Top ${data?.rules?.highSpenderTopPercent || draftFilters.topPercent}% by spend`,
      },
      {
        title: 'High / dormant risk',
        value: formatNumber((churnCounts.high || 0) + (churnCounts.dormant || 0)),
        change: '—',
        trend: 'neutral',
        context: 'Need re-engagement',
      },
      {
        title: 'Avg churn score',
        value: String(summary.averageChurnRiskScore ?? 0),
        change: '—',
        trend: 'neutral',
        context: '0 low · 100 highest',
      },
    ],
    [
      summary.customerCount,
      summary.highSpenderCount,
      summary.averageChurnRiskScore,
      churnCounts.high,
      churnCounts.dormant,
      data?.period?.start,
      data?.period?.end,
      data?.rules?.highSpenderTopPercent,
      draftFilters.topPercent,
    ]
  );

  function handleDraftChange(key, value) {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  }

  function handleApply() {
    setFilters(draftFilters);
  }

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      {loading ? (
        <PageSummaryStats stats={[]} isFirstLoad />
      ) : !error ? (
        <PageSummaryStats stats={summaryStats} isFirstLoad={false} />
      ) : null}

      {/* Filters — same surface language as JourneyFilterBar */}
      <div className="rounded-xl border border-slate-200 bg-[#F3F5F8] px-3 py-3 sm:px-4">
        <div className="flex flex-wrap items-end gap-2">
          <FilterField label="Start">
            <input
              type="date"
              value={draftFilters.start}
              onChange={(event) => handleDraftChange('start', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="End">
            <input
              type="date"
              value={draftFilters.end}
              onChange={(event) => handleDraftChange('end', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Top %">
            <input
              type="number"
              min="1"
              max="100"
              value={draftFilters.topPercent}
              onChange={(event) => handleDraftChange('topPercent', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>
          <FilterField label="Rows">
            <input
              type="number"
              min="1"
              max="500"
              value={draftFilters.limit}
              onChange={(event) => handleDraftChange('limit', event.target.value)}
              className={filterControlClass}
            />
          </FilterField>

          <DataSourceChip label={LIVE_DATA_SOURCE_LABEL} className="h-9 shrink-0 self-end" />

          <Button
            type="button"
            size="sm"
            onClick={handleApply}
            className="h-9 shrink-0 self-end !rounded-2xl bg-[var(--brand-primary,#1A428A)] hover:bg-[#163872] sm:min-w-[88px]"
          >
            Apply
          </Button>
        </div>
      </div>

      {!loading && (needsTenant || error) ? (
        <PanelStateMessage
          {...(needsTenant || error === 'NEEDS_TENANT'
            ? {
                variant: 'info',
                icon: Buildings,
                title: 'Select a tenant first',
                message:
                  'Open a customer account from Tenants so we know whose segment scores to show.',
              }
            : friendlySegmentsError(error))}
        />
      ) : null}

      {syncResult ? (
        <ConfidenceStrip
          tone={syncResult.type === 'success' ? 'success' : 'warning'}
          title={syncResult.message}
        />
      ) : null}

      {!loading && !error ? (
        <>
          <ConfidenceStrip
            tone="success"
            title="Privacy: customer_id and segment scores only — no names, emails, or phone numbers."
          />

          <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(260px,0.7fr)]">
            <div
              className="flex min-h-0 min-w-0 flex-col gap-2"
              style={
                isWideLayout && sideRailHeight
                  ? { height: sideRailHeight }
                  : undefined
              }
            >
              <Card className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                <CardHeader className="shrink-0 px-4 py-3">
                  <CardTitle>Customer segment scores</CardTitle>
                  <CardDescription>
                    Sorted by total spend · {formatNumber(segments.length)} customers in result
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
                  {segments.length ? (
                    <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
                      <table className="w-max min-w-[72rem] table-fixed text-left text-sm">
                        <colgroup>
                          <col className="w-[7.5rem]" />
                          <col className="w-[8.5rem]" />
                          <col className="w-[7.5rem]" />
                          <col className="w-[5.5rem]" />
                          <col className="w-[5.5rem]" />
                          <col className="w-[13rem]" />
                          <col className="w-[8.5rem]" />
                          <col className="w-[9.5rem]" />
                          <col className="w-[8rem]" />
                        </colgroup>
                        <thead className="sticky top-0 z-10 border-b border-slate-200 bg-[#F7F9FC] text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                          <tr>
                            <th className="sticky left-0 z-20 bg-[#F7F9FC] px-4 py-3">ID</th>
                            <th className="px-4 py-3">Spend</th>
                            <th className="px-4 py-3">AOV</th>
                            <th className="px-4 py-3">%ile</th>
                            <th className="px-4 py-3">High</th>
                            <th className="px-4 py-3">Behavior</th>
                            <th className="px-4 py-3">Activity</th>
                            <th className="px-4 py-3">Churn</th>
                            <th className="px-4 py-3">Last seen</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {pageRows.map((segment) => (
                            <tr
                              key={`${segment.accountId}-${segment.customerId}-${segment.currencyCode}`}
                              className="group text-slate-700 hover:bg-slate-50/80"
                            >
                              <td className="sticky left-0 z-10 bg-white px-4 py-3 font-semibold tabular-nums text-[#201C35] group-hover:bg-slate-50">
                                {segment.customerId}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap tabular-nums font-medium">
                                {formatMoney(segment.totalSpend, segment.currencyCode)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                                {formatMoney(segment.averageOrderValue, segment.currencyCode)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap tabular-nums text-slate-600">
                                {segment.spendPercentile}%
                              </td>
                              <td className="px-4 py-3">
                                {segment.highSpender ? (
                                  <span className="inline-flex rounded-full bg-[var(--brand-primary,#1A428A)]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[var(--brand-primary,#1A428A)] ring-1 ring-[var(--brand-primary,#1A428A)]/20">
                                    Yes
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-300">—</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <BehaviorTags segments={segment.behaviorSegments} />
                              </td>
                              <td className="px-4 py-3">
                                <ActivityCell segment={segment} />
                              </td>
                              <td
                                className="px-4 py-3"
                                title={
                                  [segment.churnRiskReason, ...(segment.churnRiskFactors || [])]
                                    .filter(Boolean)
                                    .map((part) => String(part).replaceAll('_', ' '))
                                    .join(' · ') || undefined
                                }
                              >
                                <div className="flex flex-wrap items-center gap-2 whitespace-nowrap">
                                  <ChurnBadge level={segment.churnRiskLevel} />
                                  <span className="text-[11px] tabular-nums text-slate-500">
                                    {segment.churnRiskScore}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="text-sm tabular-nums">{segment.lastActivityDate}</div>
                                <div className="text-[11px] text-slate-400">
                                  {segment.daysSinceLastActivity}d ago
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <PanelStateMessage
                      title="No customer segments yet"
                      message="No rows with customer_id matched the selected period."
                    />
                  )}
                </CardContent>
              </Card>

              {segments.length ? (
                <div className="shrink-0">
                  <TablePagination
                    page={safePage}
                    pageCount={pageCount}
                    pageSize={PAGE_SIZE}
                    total={segments.length}
                    onChange={setPage}
                  />
                </div>
              ) : null}
            </div>

            <div ref={sideRailRef} className="flex min-w-0 flex-col gap-4">
              <Card>
                <CardHeader className="px-4 py-3">
                  <CardTitle>Churn risk mix</CardTitle>
                  <CardDescription>Rule-based recency, frequency, spend, and activity signals.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {['low', 'medium', 'high', 'dormant'].map((level) => (
                    <div key={level} className="flex items-center justify-between gap-3">
                      <ChurnBadge level={level} />
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-[var(--brand-primary,#1A428A)]"
                          style={{
                            width: `${
                              summary.customerCount
                                ? ((churnCounts[level] || 0) / summary.customerCount) * 100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                      <span className="w-10 text-right text-sm font-semibold tabular-nums text-slate-700">
                        {formatNumber(churnCounts[level] || 0)}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="px-4 py-3">
                  <CardTitle>Top high spenders</CardTitle>
                  <CardDescription>Highest spenders flagged in this window.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {highSpenderCandidates.length ? (
                    highSpenderCandidates.map((segment) => (
                      <div
                        key={segment.customerId}
                        className="flex items-center justify-between gap-3 rounded-lg bg-[#F7F9FC] px-3 py-2"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-[#201C35]">
                            Customer {segment.customerId}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {segment.invoiceCount} invoices
                          </div>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-sm font-semibold tabular-nums text-slate-800">
                            {formatMoney(segment.totalSpend, segment.currencyCode)}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {segment.spendPercentile}%ile
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-slate-500">No high spenders in this result set.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
